
import { useRef, useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { FEEDBACK_KIND_LABELS, type OwnFeedbackDTO } from '@/lib/feedback'
import { formatDate } from '@/lib/format'
import { btn, cx, inputSm, notice, segment } from '@/lib/ui'
import { useDetailsPopover } from '@/lib/use-details-popover'
import { errorMessage } from '@/lib/client-api'

const KINDS = ['BUG', 'DEVICE_REQUEST', 'FEATURE', 'OTHER'] as const

/**
 * Sending a report, and seeing what came of the last ones.
 *
 * Built as a `<details>` popover rather than a modal because that is the
 * pattern the rail already uses twice — no focus trap, no scroll lock, no
 * backdrop primitive, and it closes on navigation for free.
 *
 * The submitter's own list is the point as much as the form is. A report that
 * disappears into silence teaches people not to send the next one, so the
 * reply written when it is resolved comes back here.
 */
export function FeedbackMenu({
  summaryClass,
  popoverClass,
}: {
  summaryClass: string
  popoverClass: string
}) {
  const ref = useRef<HTMLDetailsElement>(null)
  const [kind, setKind] = useState<(typeof KINDS)[number]>('BUG')
  const [message, setMessage] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [mine, setMine] = useState<OwnFeedbackDTO[] | null>(null)

  // Nothing is fetched until the popover is opened: this component renders on
  // every page in the app, and a request on mount would put a console error on
  // every screen the moment anything went wrong.
  async function loadMine() {
    try {
      const response = await fetch('/api/feedback?mine=1')
      if (response.ok) setMine((await response.json()) as OwnFeedbackDTO[])
    } catch {
      // A failed history load must not bury the form underneath it.
    }
  }

  async function send() {
    if (!message.trim()) return
    setState('sending')
    setError(null)
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, message: message.trim() }),
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(body?.error ?? 'Could not send that')
      }
      setMessage('')
      setState('sent')
      void loadMine()
    } catch (cause) {
      setState('idle')
      setError(errorMessage(cause, 'Could not send that'))
    }
  }

  useDetailsPopover(ref)


  return (
    <details
      ref={ref}
      className="relative"
      onToggle={(event) => {
        if ((event.currentTarget as HTMLDetailsElement).open && mine === null) void loadMine()
      }}
    >
      <summary className={summaryClass} aria-haspopup="true" aria-label="Send feedback" title="Send feedback">
        <Icon name="sparkle" className="h-5 w-5" />
      </summary>

      <div className={cx(popoverClass, 'w-80 max-w-[calc(100vw-1.5rem)] p-3')}>
        <p className="text-sm font-medium">Send feedback</p>
        <p className="mt-0.5 text-[11px] text-muted">
          Bugs, missing equipment, or anything you wish this did.
        </p>

        <div className="mt-2.5 flex flex-wrap gap-1">
          {KINDS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setKind(option)}
              aria-pressed={kind === option}
              className={cx(segment.base, kind === option ? segment.on : segment.off)}
            >
              {FEEDBACK_KIND_LABELS[option]}
            </button>
          ))}
        </div>

        <textarea
          value={message}
          onChange={(event) => {
            setMessage(event.target.value)
            if (state === 'sent') setState('idle')
          }}
          maxLength={4000}
          rows={4}
          aria-label="What happened"
          placeholder={
            kind === 'DEVICE_REQUEST'
              ? 'e.g. you are missing the AVMatrix PVS0615U switcher'
              : kind === 'BUG'
                ? 'What were you doing, and what happened instead?'
                : 'What would help?'
          }
          className={cx(inputSm, 'mt-2.5 w-full resize-y')}
        />

        {error && (
          <p role="alert" className={cx(notice.error, 'mt-2 text-[11px]')}>
            {error}
          </p>
        )}
        {state === 'sent' && (
          <p className={cx(notice.success, 'mt-2 text-[11px]')}>
            Got it. Anything that comes of it shows up in the list below.
          </p>
        )}

        <button
          type="button"
          onClick={() => void send()}
          disabled={state === 'sending' || message.trim().length === 0}
          className={cx(btn.sm, btn.primary, 'mt-2.5 w-full')}
        >
          {state === 'sending' ? 'Sending…' : 'Send feedback'}
        </button>

        {mine && mine.length > 0 && (
          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto border-t border-[var(--border)] pt-2.5">
            {mine.map((item) => (
              <div key={item.id} className="text-[11px]">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-muted">{item.message}</span>
                  <span
                    className={cx(
                      'shrink-0 font-medium',
                      item.resolvedAt ? 'text-success' : 'text-muted',
                    )}
                  >
                    {item.resolvedAt ? 'Done' : 'Sent'}
                  </span>
                </div>
                {item.reply && (
                  <p className="mt-0.5 rounded-lg bg-[var(--surface-raised)]/70 px-2 py-1 text-foreground">
                    {item.reply}
                    <span className="ml-1 text-muted">· {formatDate(item.resolvedAt)}</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  )
}
